import React, { useState } from 'react';
import { X, ExternalLink, BookOpen } from 'lucide-react';
import contextHelpConfig from '../../help/contextHelpConfig';
import { getArticleById, isArticleEnabled } from '../../help/articleIndex';

interface ContextHelpDrawerProps {
  pageName: string;
  open: boolean;
  onClose: () => void;
  onNavigateToHelp: () => void;
  onNavigateToArticle?: (articleId: string, category: string) => void;
  enabledFeatures?: Record<string, boolean>;
}

const ContextHelpDrawer: React.FC<ContextHelpDrawerProps> = ({
  pageName, open, onClose, onNavigateToHelp, onNavigateToArticle, enabledFeatures,
}) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  if (!open) return null;

  const config = contextHelpConfig[pageName];
  const title = config?.title || 'Help';
  const articles = (config?.articleIds || [])
    .map(id => getArticleById(id))
    .filter((article): article is NonNullable<typeof article> => {
      if (!article) return false;
      if (!enabledFeatures) return true;
      return isArticleEnabled(article, enabledFeatures);
    });

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)', zIndex: 1999,
        }}
      />
      {/* Drawer */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '360px', maxWidth: '90vw',
        backgroundColor: 'white', boxShadow: '-4px 0 12px rgba(0,0,0,0.08)', zIndex: 2000,
        display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.25s ease-out',
      }}>
        <style>{`
          @keyframes slideInRight {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
        `}</style>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px', borderBottom: '1px solid #E5E7EB',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <BookOpen size={20} color="#3b82f6" />
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', margin: 0 }}>{title}</h3>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#6b7280' }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Articles */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {articles.length === 0 ? (
            <p style={{ fontSize: '14px', color: '#9ca3af' }}>No help articles available for this page.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {articles.map(article => {
                if (!article) return null;
                const isHovered = hoveredId === article.id;
                return (
                  <div
                    key={article.id}
                    onClick={() => {
                      if (onNavigateToArticle) {
                        onNavigateToArticle(article.id, article.category);
                        onClose();
                      }
                    }}
                    onMouseEnter={() => setHoveredId(article.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    style={{
                      padding: '12px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: isHovered ? '#F3F4F6' : 'transparent',
                      transition: 'background 0.15s',
                    }}
                  >
                    <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827', marginBottom: '4px' }}>
                      {article.title}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.4 }}>
                      {article.description}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #E5E7EB' }}>
          <button
            onClick={() => { onNavigateToHelp(); onClose(); }}
            style={{
              width: '100%', padding: '10px', backgroundColor: '#EFF6FF', color: '#3b82f6',
              border: '1px solid #BFDBFE', borderRadius: '8px', fontSize: '14px', fontWeight: 500,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            }}
          >
            <ExternalLink size={16} />
            Open Full Help Center
          </button>
        </div>
      </div>
    </>
  );
};

export default ContextHelpDrawer;
